import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import BASE_QUERY from '../baseQuery';
import {
	addDoc,
	collection,
	doc,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	setDoc,
	Timestamp,
	where,
} from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import { generateAIEmailSteps, generateSummarySteps, isFileExtractType } from './helper';
import { getActingUser } from '../user/userApi';
import { DEALS_COLLECTION } from '../deals/helper';
import { dealsApi } from '../deals/dealsApi';
import { removeUnsafeMarkdownLinks } from '@/utils/markdown';
import {
	AI_EMAIL_WORKFLOW_TYPES,
	AI_WORKFLOW_STATUSES,
	AI_WORKFLOWS_COLLECTION,
} from './constants';

// Creating an API service using RTK Query
export const aiWorkflowApi = createApi({
	reducerPath: 'aiWorkflowApi',
	tagTypes: ['AI_WORKFLOW', 'AI_WORKFLOWS'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		getAIWorkflowById: builder.query({
			providesTags: (result, error, arg) => [
				{
					type: 'AI_WORKFLOW',
					id: arg.docId,
				},
			],
			queryFn: async ({ docId }, { getState, dispatch }) => {
				try {
					const collectionRef = collection(firestore, AI_WORKFLOWS_COLLECTION);
					const resDoc = await getDoc(doc(collectionRef, docId));

					if (!resDoc) return { data: null };

					const stepsRef = collection(
						firestore,
						AI_WORKFLOWS_COLLECTION,
						resDoc.id,
						'steps',
					);

					const stepsQuery = query(stepsRef, orderBy('index'));
					const stepsSnapshot = await getDocs(stepsQuery);

					const response = {
						id: resDoc.id,
						...JSON.parse(JSON.stringify(resDoc.data())),
						dateCreated: resDoc.data()?.dateCreated?.toMillis?.() ?? null,
						lastRun: resDoc.data()?.lastRun?.toMillis?.() ?? null,
						steps: stepsSnapshot.docs.map((step) => {
							return {
								id: step.id,
								...JSON.parse(JSON.stringify(step.data())),
								dateCreated: step.data()?.dateCreated?.toMillis?.() ?? null,
								lastRun: step.data()?.lastRun?.toMillis?.() ?? null,
							};
						}),
					};
					// console.log('getAIWorkflowById response', response);
					return {
						data: response,
					};
				} catch (e) {
					console.error('getAIWorkflowById error', e);
					return { error: { code: e?.code, message: e?.message } };
				}
			},
		}),
		getUserAIWorkflows: builder.query({
			providesTags: (result, error, arg) => ['AI_WORKFLOWS'],
			// providesTags: (result, error, arg) => [
			// 	{
			// 		type: 'AI_WORKFLOWS',
			// 		id: `${arg?.userId}-${arg.complete ? String(arg.complete) : 'false'}`,
			// 	},
			// ],
			queryFn: async ({ userId, complete }, { getState, dispatch }) => {
				try {
					const collectionRef = collection(firestore, AI_WORKFLOWS_COLLECTION);

					const constraints = [
						where('uploadedBy', '==', userId),

						where('status', complete ? '==' : '!=', AI_WORKFLOW_STATUSES.SUCCESS),

						orderBy('dateCreated', 'desc'),
						limit(100),
					];

					const q = query(collectionRef, ...constraints);
					const snapshot = await getDocs(q);

					const res = await Promise.all(
						snapshot.docs.map(async (resDoc) => {
							const stepsRef = collection(
								firestore,
								AI_WORKFLOWS_COLLECTION,
								resDoc.id,
								'steps',
							);

							const stepsQuery = query(stepsRef, orderBy('index'));
							const stepsSnapshot = await getDocs(stepsQuery);

							return {
								id: resDoc.id,
								...JSON.parse(JSON.stringify(resDoc.data())),
								dateCreated: resDoc.data()?.dateCreated?.toMillis?.() ?? null,
								lastRun: resDoc.data()?.lastRun?.toMillis?.() ?? null,
								steps: stepsSnapshot.docs.map((step) => {
									return {
										id: step.id,
										...JSON.parse(JSON.stringify(step.data())),
										dateCreated: step.data()?.dateCreated?.toMillis?.() ?? null,
										lastRun: step.data()?.lastRun?.toMillis?.() ?? null,
									};
								}),
							};
						}),
					);

					// console.log('getUserAIWorkflows response', res);
					return {
						data: res,
					};
				} catch (e) {
					console.error('getUserAIWorkflows error', e);
					return { error: { code: e?.code, message: e?.message } };
				}
			},
		}),
		createAIWorkflow: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{
					type: 'AI_WORKFLOW',
					id: `${arg?.dealId}-${arg?.workflowType}`,
				},
			],
			queryFn: async (
				{
					workflowType,
					dealId,
					aiMessageIds = [],
					emailUser = false,
					filePaths = [],
					useUnlayerTemplate = false,
				},
				{ getState, dispatch },
			) => {
				console.log('createAIWorkflow body', {
					workflowType,
					dealId,
					aiMessageIds,
					emailUser,
					filePaths,
					useUnlayerTemplate,
				});
				try {
					const userData = await getActingUser(dispatch, getState);

					const fileExtract = isFileExtractType(workflowType);
					if (
						!userData?.userId ||
						!dealId ||
						!workflowType ||
						(fileExtract ? filePaths.length === 0 : aiMessageIds.length === 0)
					) {
						throw new Error('Missing required fields');
					}

					const userCollection = collection(firestore, 'user');
					const userRef = doc(userCollection, userData.userId);

					const dealData = await dispatch(
						dealsApi.endpoints.getDealRow.initiate(dealId),
					).unwrap();
					const dealRef = doc(collection(firestore, DEALS_COLLECTION), dealData.id);

					const aiMessageCollection = collection(firestore, 'aiMessages');

					const promptTypeRegex = /riskInfo(\w+)Email/;
					const match = workflowType.match(promptTypeRegex);
					console.log('Match', match);
					const workflowName = match?.[1] ?? workflowType;

					const collectionRef = collection(firestore, AI_WORKFLOWS_COLLECTION);
					const aiMessages = aiMessageIds.map((id) => doc(aiMessageCollection, id));

					const steps = isFileExtractType(workflowType)
						? generateSummarySteps({ filePaths, workflowType })
						: generateAIEmailSteps({ workflowType, aiMessages });

					if (steps.length === 0) {
						throw new Error(`Could not create workflow ${workflowType} with no steps`);
					}

					console.log('Steps', steps);
					const body = {
						dateCreated: Timestamp.now(),
						uploadedBy: userData.userId,
						userRef,
						dealId,
						dealRef,
						workflowType,
						workflowName,
						status: AI_WORKFLOW_STATUSES.UNINITIALISED,
						emailUser,
					};

					console.log('createAIWorkflow body', body);

					const newDoc = doc(collectionRef);
					await setDoc(newDoc, body);

					const stepsCollectionRef = collection(newDoc, 'steps');

					for (const step of steps) {
						await addDoc(stepsCollectionRef, {
							...step,
							dateCreated: Timestamp.now(),
						});
					}

					return {
						data: {
							success: true,
							docId: newDoc.id,
							message: 'New AI workflow created successfully',
						},
					};
				} catch (error) {
					console.error('Error creating AI workflow', error);
					return { error: { status: 500, message: error.message } };
				}
			},
		}),
		// Saves a user-edited summary as a new aiMessages doc. userCreated stops the backend
		// aiMessages trigger from re-running the pre-renewal automations.
		createUserAIMessage: builder.mutation({
			queryFn: async ({ dealId, summaryType, message, editedFrom }, { getState, dispatch }) => {
				try {
					const userData = await getActingUser(dispatch, getState);

					if (!userData?.userId || !dealId || !summaryType) {
						throw new Error('Missing required fields');
					}

					const newDoc = doc(collection(firestore, 'aiMessages'));
					await setDoc(newDoc, {
						dealId,
						summaryType,
						message: removeUnsafeMarkdownLinks(message),
						dateCreated: Timestamp.now(),
						userCreated: true,
						userId: userData.userId,
						editedFrom,
					});

					return { data: { docId: newDoc.id } };
				} catch (error) {
					console.error('Error saving AI message', error);
					return { error: { status: 500, message: error.message } };
				}
			},
		}),
	}),
});

export const {
	useGetAIWorkflowByIdQuery,
	useLazyGetAIWorkflowByIdQuery,
	useGetUserAIWorkflowsQuery,
	useCreateAIWorkflowMutation,
	useCreateUserAIMessageMutation,
} = aiWorkflowApi;
